import React from 'react';
import { XMarkIcon, CheckIcon, EyeIcon, ChartBarIcon, ShieldCheckIcon, DocumentTextIcon, MapPinIcon, MapIcon } from '@heroicons/react/24/outline';
import { createSafeMarkdown } from '../utils/safeMarkdown';
import AnalysisResultDisplay from './AnalysisResultDisplay';
import { isSrmPlanWorkflow, srmModuleReportData, srmModuleReportGaps } from '../../functions/src/common/srmModuleReports';
import SrmPlanDocumentDisplay from './reports/SrmPlanDocumentDisplay';
import type { SrmModule, SrmProject } from '@types';

const md = createSafeMarkdown({
    html: true,
    linkify: true,
    typographer: true
});

interface SrmModuleReviewModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApprove?: () => void;
    moduleName: string;
    resultData: any;
    workflowId?: string;
    readOnly?: boolean;
    onRegenerate?: () => Promise<void>;
    isRegenerating?: boolean;
    regenerationError?: string;
    reportGaps?: string[];
    organizationId?: string;
    srmProject?: SrmProject;
    srmModule?: SrmModule;
}

const SrmModuleReviewModal: React.FC<SrmModuleReviewModalProps> = ({ 
    isOpen, 
    onClose, 
    onApprove, 
    moduleName, 
    resultData,
    workflowId,
    readOnly = false,
    onRegenerate,
    isRegenerating = false,
    regenerationError,
    reportGaps,
    organizationId,
    srmProject,
    srmModule,
}) => {
    if (!isOpen) return null;
    resultData = srmModuleReportData(resultData);
    const gaps = reportGaps || srmModuleReportGaps({ workflowId, resultData });

    const renderStructuredData = (data: any) => {
        const hasFindings = data.findings && Array.isArray(data.findings);
        const hasSections = data.sections && Array.isArray(data.sections);
        const hasPmesii = !!data.pmesii;
        const hasThreatGroups = data.threatGroups && Array.isArray(data.threatGroups);
        const hasMedical = !!(data.medicalAssessment || data.healthData);
        const hasFsatScore = typeof data.totalScore === 'number' || typeof data.score === 'number';

        return (
            <div className="space-y-8 pb-10">
                {/* Score Banner */}
                {hasFsatScore && (
                    <div className="p-6 bg-slate-900 rounded-2xl border border-slate-700 shadow-xl overflow-hidden relative group">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl -mr-10 -mt-10 group-hover:bg-blue-500/20 transition-all"></div>
                        <div className="relative z-10 flex items-center justify-between">
                            <div>
                                <h4 className="text-[10px] font-bold text-blue-400 uppercase tracking-[0.2em] mb-1">Security Assessment Score</h4>
                                <div className="text-4xl font-black text-white flex items-baseline gap-1">
                                    {data.totalScore || data.score}
                                    <span className="text-xs text-slate-500 font-bold uppercase ml-1">Percent Compliance</span>
                                </div>
                            </div>
                            <div className={`p-4 rounded-xl border ${data.riskLevel === 'High' ? 'bg-red-500/20 border-red-500/30' : 'bg-green-500/20 border-green-500/30'}`}>
                                <ShieldCheckIcon className={`w-8 h-8 ${data.riskLevel === 'High' ? 'text-red-400' : 'text-green-400'}`} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Threat Groups */}
                {hasThreatGroups && (
                    <div className="grid gap-4">
                        <div className="flex items-center gap-2 mb-2">
                            <ChartBarIcon className="w-5 h-5 text-red-600" />
                            <h4 className="text-sm font-bold text-slate-900 uppercase tracking-tight">Active Threat Actors</h4>
                        </div>
                        {data.threatGroups.map((group: any, idx: number) => (
                            <div key={idx} className="p-5 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md transition-all">
                                <div className="flex justify-between items-start mb-3">
                                    <h5 className="font-bold text-slate-900 text-base">{group.name || group.title}</h5>
                                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-[10px] font-bold uppercase">{group.threatLevel || 'Active'}</span>
                                </div>
                                <p className="text-xs text-slate-600 leading-relaxed italic border-l-2 border-slate-100 pl-4">{group.description || group.overview}</p>
                            </div>
                        ))}
                    </div>
                )}

                {/* Analysis Indicators */}
                {(hasPmesii || data.geographicData) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="p-5 bg-blue-50/50 border border-blue-100 rounded-2xl">
                            <div className="flex items-center gap-2 mb-4">
                                <MapIcon className="w-5 h-5 text-blue-600" />
                                <h4 className="text-xs font-bold text-blue-900 uppercase tracking-widest">Environmental Indicators</h4>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {data.pmesii && Object.entries(data.pmesii).map(([key, value]: [string, any], idx) => (
                                    <div key={idx} className="p-3 bg-white/80 rounded-lg border border-blue-200/50">
                                        <h5 className="text-[9px] font-bold text-blue-500 uppercase mb-1">{key}</h5>
                                        <p className="text-[10px] text-slate-600 line-clamp-2">{value}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        {hasMedical && (
                            <div className="p-5 bg-green-50/30 border border-green-200/50 rounded-2xl">
                                <div className="flex items-center gap-2 mb-4">
                                    <ShieldCheckIcon className="w-5 h-5 text-green-600" />
                                    <h4 className="text-xs font-bold text-green-900 uppercase tracking-widest">Medical Response Assets</h4>
                                </div>
                                <div className="text-xs text-slate-700 leading-relaxed">
                                    {typeof (data.medicalAssessment || data.healthData)?.summary === 'string' ? 
                                        (data.medicalAssessment || data.healthData).summary : 
                                        "Available local trauma care and emergency assets identified."
                                    }
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Sections */}
                {hasSections && data.sections.map((section: any, idx: number) => (
                    <div key={idx} className="mt-6 border-b border-slate-100 pb-8 last:border-0">
                        <h4 className="text-sm font-bold text-slate-900 mb-2 border-l-4 border-blue-500 pl-3">{section.heading || section.title}</h4>
                        <div className="prose prose-sm text-slate-700 max-w-none ml-4" dangerouslySetInnerHTML={{ __html: md.render(section.content || section.body || "") }} />
                    </div>
                ))}

                {/* Findings */}
                {hasFindings && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-4 border-b border-slate-100 pb-2">
                            <ChartBarIcon className="w-5 h-5 text-blue-600" />
                            <h4 className="text-sm font-bold text-slate-900 uppercase tracking-tight">Key Investigative Findings</h4>
                        </div>
                        <div className="grid gap-3">
                            {data.findings.map((f: any, idx: number) => (
                                <div key={idx} className="p-4 bg-slate-50 border border-slate-200 rounded-xl hover:bg-white transition-colors cursor-default">
                                    <p className="font-bold text-slate-900 mb-1">{f.title || f.finding || `Detail ${idx + 1}`}</p>
                                    <p className="text-xs text-slate-600">{f.description || f.content || (typeof f === 'string' ? f : JSON.stringify(f))}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Recommendations */}
                {data.recommendations && Array.isArray(data.recommendations) && (
                    <div className="mt-8">
                        <div className="flex items-center gap-2 mb-4 border-b border-amber-100 pb-2">
                            <ShieldCheckIcon className="w-5 h-5 text-amber-600" />
                            <h4 className="text-sm font-bold text-slate-900 uppercase tracking-tight">Strategic Recommendations</h4>
                        </div>
                        <ul className="space-y-3">
                            {data.recommendations.map((r: any, idx: number) => (
                                <li key={idx} className="flex gap-4 p-4 bg-amber-50/30 border border-amber-100 rounded-xl">
                                    <div className="h-6 w-6 flex-shrink-0 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold">{idx + 1}</div>
                                    <div>
                                        <p className="font-bold text-slate-800 text-sm mb-1">{r.title || (typeof r === 'string' ? r : `Recommendation ${idx + 1}`)}</p>
                                        {(r.description || r.content) && <p className="text-xs text-slate-600">{r.description || r.content}</p>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    const renderContent = () => {
        if (!resultData) return <div className="text-slate-500 italic flex items-center justify-center py-20 bg-slate-50 rounded-2xl border border-dashed border-slate-200">No data generated yet.</div>;
        if (isSrmPlanWorkflow(workflowId || ''))
            return <SrmPlanDocumentDisplay result={resultData} title={moduleName} />;

        const activeData = workflowId === 'threat_group_network_analysis' && !resultData.groupName &&
            (resultData.actors || resultData.threatGroups) ? { ...resultData, reportType: 'IntegratedAnalysisResult' } : resultData;

        // 0. Handle Intelligence Report Profiles (Specialized Rendering)
        // Check for common intelligence report types or structure
        const isIntelReport = activeData.reportType === 'IntegratedAnalysisResult' || 
                             activeData.reportType === 'IntegratedAnalysis' ||
                             activeData.reportType === 'Error' ||
                             (typeof activeData === 'object' && !Array.isArray(activeData) &&
                                 (workflowId === 'integrated_threat_analysis' || workflowId === 'country_geopolitical_analysis')) ||
                             activeData.reportType === 'GeopoliticalAnalysis' || 
                             activeData.reportType === 'CompositeReportResult' ||
                             activeData.reportType === 'CountryRiskProfile' ||
                             activeData.reportType === 'DailyMicroReport' ||
                             activeData.reportType === 'GlobalThreatGroupAnalysis' ||
                             activeData.reportType === 'EpThreatAnalysis' ||
                             activeData.reportType === 'EpThreatAnalysisResult' ||
                             activeData.reportType === 'FacilityProtectionResult' ||
                             activeData.reportType === 'EventSecurityPlanResult' ||
                             activeData.reportType === 'TravelSecurityBriefResult' ||
                             activeData.reportType === 'ItineraryRiskResult' ||
                             activeData.reportType === 'AuditRemediationResult' ||
                             activeData.candidates || // Raw Gemini responses should go through the display engine
                             (activeData.sections && activeData.summary) ||
                             (activeData.pmesii && activeData.riskScore);

        if (isIntelReport) {
            return (
                <div className="bg-slate-50 rounded-3xl p-8 border border-slate-100 shadow-inner min-h-[600px]">
                    <AnalysisResultDisplay result={activeData} activeWorkflow={workflowId} readOnly={readOnly}
                        organizationId={organizationId || srmProject?.organizationId} srmProject={srmProject} srmModule={srmModule} />
                </div>
            );
        }

        // 1. Handle string/markdown results (Direct)
        if (typeof resultData === 'string') {
            return (
                <div className="prose prose-slate max-w-none px-4 py-2" dangerouslySetInnerHTML={{ __html: md.render(resultData) }} />
            );
        }

        // 2. Handle object with .content property (SrmFramework, SrmDocument, LessonPlan)
        if (resultData.content && typeof resultData.content === 'string') {
            return (
                <div className="prose prose-slate max-w-none px-4 py-2" dangerouslySetInnerHTML={{ __html: md.render(resultData.content) }} />
            );
        }

        // 3. Handle Arrays (New: Direct list of objects)
        if (Array.isArray(resultData)) {
            return renderStructuredData({ findings: resultData });
        }

        // 4. Handle FSAT style results with specific findings key
        if (resultData.findings || resultData.recommendations || resultData.executiveSummary || resultData.summary || resultData.pmesii || resultData.threatGroups) {
            return renderStructuredData(resultData);
        }

        // 5. Default JSON view for anything else (Fallback)
        return (
            <div className="space-y-4">
                <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
                    <ChartBarIcon className="w-5 h-5" />
                    <span className="text-xs font-bold uppercase">Generic Analysis Payload Detected</span>
                </div>
                <pre className="bg-slate-900 text-blue-300 p-6 rounded-2xl overflow-auto text-[11px] font-mono max-h-[500px] border border-slate-700 shadow-inner custom-scrollbar">
                    {JSON.stringify(resultData, null, 2)}
                </pre>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
            <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden border border-white/20 animate-in zoom-in-95 duration-300">
                {/* Header */}
                <div className="px-8 py-6 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-500/30">
                            <EyeIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="text-xl font-black text-slate-900 tracking-tight">{readOnly ? 'View Insights Analysis' : 'Review Insights Analysis'}</h3>
                            <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mt-1 opacity-80">{moduleName}</p>
                        </div>
                    </div>
                    {!readOnly && onRegenerate && <button type="button" onClick={() => void onRegenerate()} disabled={isRegenerating}
                        aria-label={`Regenerate ${moduleName}`}
                        className="ml-auto mr-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:bg-slate-400">
                        {isRegenerating ? 'Regenerating…' : 'Regenerate'}
                    </button>}
                    <button
                        onClick={onClose} 
                        className="p-3 hover:bg-slate-100 rounded-full transition-all group hover:rotate-90 duration-300"
                    >
                        <XMarkIcon className="w-6 h-6 text-slate-400 group-hover:text-slate-900" />
                    </button>
                </div>

                {/* Content Container */}
                <div className={`flex-1 overflow-y-auto bg-white custom-scrollbar ${isSrmPlanWorkflow(workflowId || '') ? 'p-4 sm:p-8' : 'p-10'}`}>
                    <div className="max-w-4xl mx-auto">
                        <div className="mb-10 p-5 bg-blue-50 border border-blue-100 rounded-2xl flex gap-4 items-start shadow-sm">
                            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                                <ShieldCheckIcon className="w-6 h-6 text-blue-600" />
                            </div>
                            <p className="text-sm font-medium text-slate-700 leading-relaxed">
                                <span className="font-bold text-blue-900 block mb-0.5 uppercase text-[10px] tracking-widest">{readOnly ? 'Consultant-Managed Intelligence' : 'Autonomous Intelligence Advisory'}</span>
                                {readOnly ? 'This delivered analysis remains synchronized with your consultant-managed SRM framework.' : <>This content was autonomously generated by the <span className="text-blue-600 font-bold">Insights SRM Auto Architect</span> based on your geographical and organizational context. Please review and verify the findings before approval.</>}
                            </p>
                        </div>

                        <div className="animate-in slide-in-from-bottom-2 duration-700 delay-150">
                            {gaps.length > 0 && <div role="status" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                                <p className="font-bold">Report gaps need review</p>
                                {gaps.map(gap => <p key={gap} className="mt-2">{gap}</p>)}
                            </div>}
                            {!readOnly && regenerationError && <p role="alert" className="mb-4 text-sm text-red-700">{regenerationError}</p>}
                            {renderContent()}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-10 py-6 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-4">
                    <div className="flex flex-col">
                        <span className="text-[10px] uppercase font-bold text-slate-400">{readOnly ? 'Read-only access' : 'Approval Required'}</span>
                        <span className="text-xs text-slate-600">{readOnly ? 'Changes remain consultant managed' : 'Pending final synchronization with library'}</span>
                    </div>

                    <div className="flex items-center gap-3">
                        <button 
                            onClick={onClose}
                            className="px-6 py-3 rounded-2xl border border-slate-300 text-slate-700 font-bold text-sm hover:bg-white hover:shadow-md transition-all active:scale-95"
                        >
                            {readOnly ? 'Close' : 'Keep as Draft'}
                        </button>
                        {!readOnly && onApprove && <button
                            onClick={onApprove}
                            disabled={isRegenerating}
                            className="px-10 py-3 rounded-2xl bg-blue-600 text-white font-black text-sm hover:bg-blue-500 hover:shadow-xl hover:shadow-blue-500/20 transition-all flex items-center gap-2 active:scale-95"
                        >
                            <CheckIcon className="w-5 h-5 stroke-[3]" />
                            Approve Analysis
                        </button>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SrmModuleReviewModal;
